// The /welcome screen's server calls (WEB_PLAN.md §5.2). RPCs go through auth/rpc (an HTTP 401 refreshes the session
// once; errors come out as ServerError / NetworkError so they are worded like iOS). The company seed goes through the
// sync api module's `syncPush`, the same call the engine makes, since no engine runs before the account is in a company.
// Joining and creating follow SyncCenter.accept / createCompany: when the answer may have been lost, `my_memberships`
// decides whether it went through.

import { fetchMemberships } from '@/auth/memberships';
import { callRpc } from '@/auth/rpc';
import type { InvitationOffer, Membership, PushItem, PushResult } from '@/contracts/wire';
import { supabase } from '@/supabase';
import { syncPush } from '@/sync/api';
import {
  findCreatedCompany,
  findJoinedCompany,
  mayHaveCreated,
  mayHaveJoined,
  membershipFromJson,
  UnexpectedAnswerError,
} from './companySetup';
import { codeForServer } from './inviteCode';
import { parseInvitationOffers, parsePreviewOffer } from './offers';

export { UnexpectedAnswerError };

/** `my_invitations`: pending invitations for this account's confirmed email (none for phone-only accounts). */
export async function fetchInvitationOffers(): Promise<InvitationOffer[]> {
  return parseInvitationOffers(await callRpc<unknown>('my_invitations'));
}

/** `preview_invite_code`: which company and role a code leads to. Throws `invalid_code` / `invitation_expired`. */
export async function previewInviteCode(code: string): Promise<InvitationOffer> {
  const offer = parsePreviewOffer(await callRpc<unknown>('preview_invite_code', { p_code: codeForServer(code).trim() }));
  if (!offer) throw new UnexpectedAnswerError('preview_invite_code');
  return offer;
}

/**
 * `accept_invitation` (with the code when it was found with one). A `no_invitation` / `already_member` refusal or a
 * lost answer may hide a join that went through (a second click, a reply lost on the way): if this account is an
 * active member of the offer's company now, that membership is the answer.
 */
export async function acceptInvitation(offer: Pick<InvitationOffer, 'id' | 'company_id'>, code: string | null): Promise<Membership> {
  const typed = code ? codeForServer(code).trim() : '';
  try {
    const json = await callRpc<unknown>('accept_invitation', typed ? { p_invitation: offer.id, p_code: typed } : { p_invitation: offer.id });
    const m = membershipFromJson(json);
    if (!m) throw new UnexpectedAnswerError('accept_invitation');
    return m;
  } catch (e) {
    if (!mayHaveJoined(e)) throw e;
    const joined = await fetchMemberships().then((list) => findJoinedCompany(list, offer.company_id), () => null);
    if (!joined) throw e;
    return joined;
  }
}

/**
 * `create_company(p_name, p_profile_id)`. On a lost or unreadable answer or `already_member`, the company may exist
 * already (a retry of a create that did go through): an active admin membership with this profile id is adopted,
 * like iOS.
 */
export async function createCompany(name: string, profileId: string): Promise<Membership> {
  try {
    const m = membershipFromJson(await callRpc<unknown>('create_company', { p_name: name, p_profile_id: profileId }));
    if (!m) throw new UnexpectedAnswerError('create_company');
    return m;
  } catch (e) {
    if (!mayHaveCreated(e)) throw e;
    const created = await fetchMemberships().then((list) => findCreatedCompany(list, profileId), () => null);
    if (!created) throw e;
    return created;
  }
}

/** One `sync_push` call with the seed items still missing (the seed is far below the 500-item chunk size). */
export function pushSeedBatch(companyId: string, items: PushItem[]): Promise<PushResult> {
  return syncPush(supabase, companyId.toLowerCase(), items);
}
